import { inject, Injectable } from '@angular/core';
import { decodeJwt, secondsUntilExpiry } from '@umss/shared/utils';
import {
  catchError,
  finalize,
  fromEvent,
  merge,
  Observable,
  of,
  shareReplay,
  Subscription,
  switchMap,
  tap,
  throttleTime,
  timer
} from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationResponseDto } from '../../../models';
import { AuthAPIService } from './auth-api.service';

export interface RefreshTokenCallbacks {
  onSuccess?: (response: AuthenticationResponseDto) => void;
  onError?: (error: HttpErrorResponse) => void;
}

@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {
  private refreshInFlight$?: Observable<AuthenticationResponseDto>;
  private refreshSub?: Subscription;
  private activitySub?: Subscription;

  // Track clock drift: serverTime - clientTime
  private clockOffsetSeconds = 0;
  // Track current scheduled token to avoid redundant restarts
  private currentScheduledToken: string | null = null;

  // Refresh configuration
  private readonly REFRESH_THRESHOLD_SEC = 10 * 60; // 10 minutes before expiration
  private readonly ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // Check activity every 1 minute
  private readonly MIN_TIME_BEFORE_REFRESH_SEC = 15 * 60; // Only refresh if less than 15 minutes remaining

  private authAPIService = inject(AuthAPIService);

  clear(): void {
    this.refreshSub?.unsubscribe();
    this.activitySub?.unsubscribe();
  }

  /** Schedules token refresh before expiry. */
  scheduleProactiveRefresh(
    accessToken: string | null,
    callbacks: RefreshTokenCallbacks
  ) {
    // Only schedule if the token changed and we aren't already refreshing
    if (
      !accessToken ||
      accessToken === this.currentScheduledToken ||
      !!this.refreshInFlight$
    ) {
      return;
    }

    // Cancel previous scheduling
    this.refreshSub?.unsubscribe();
    this.currentScheduledToken = accessToken;

    // Use compensated time to handle client-server clock skew
    const nowCompensated =
      Math.floor(Date.now() / 1000) + this.clockOffsetSeconds;
    const seconds = secondsUntilExpiry(accessToken, nowCompensated);

    // If we don't have a valid expiry, we can't schedule
    if (seconds === null || seconds === undefined) {
      this.currentScheduledToken = null;
      return;
    }

    // Computes when it should call for the refresh token.
    const dueInSec = Math.max(seconds - this.REFRESH_THRESHOLD_SEC, 0);

    this.refreshSub = timer(dueInSec * 1000)
      .pipe(
        switchMap(() => {
          // Double check with latest state using drift compensation
          const now = Math.floor(Date.now() / 1000) + this.clockOffsetSeconds;
          const remaining = secondsUntilExpiry(accessToken, now);

          if (remaining != null && remaining <= this.REFRESH_THRESHOLD_SEC) {
            return this.refreshToken(callbacks);
          }

          return of(null);
        }),
        catchError((err) => {
          callbacks.onError?.(err);
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Attempts to refresh the access token.
   * Calibrates the clock drift on success to ensure accurate timing.
   */
  refreshToken(
    callbacks: RefreshTokenCallbacks
  ): Observable<AuthenticationResponseDto> {
    this.refreshInFlight$ =
      this.refreshInFlight$ || this.computeRefreshInFlight();

    return this.refreshInFlight$.pipe(
      tap({
        next: (res) => callbacks.onSuccess?.(res),
        error: (err) => callbacks.onError?.(err)
      })
    );
  }

  private computeRefreshInFlight() {
    return this.authAPIService.refreshToken().pipe(
      tap((res) => {
        // Calculate drift between server iat and client now
        this.calibrateClock(res.accessToken);
      }),
      finalize(() => {
        this.refreshInFlight$ = undefined;
        this.currentScheduledToken = null;
      }),
      shareReplay(1)
    );
  }

  private calibrateClock(token: string) {
    const decoded = decodeJwt(token);

    if (decoded?.iat) {
      const nowClient = Math.floor(Date.now() / 1000);
      this.clockOffsetSeconds = decoded.iat - nowClient;
    }
  }

  /**
   * Sets up user activity-based token refresh
   * Monitors mouse, keyboard, and touch events to detect activity
   */
  public setupActivityBasedRefresh(
    getToken: () => string | null,
    callbacks: RefreshTokenCallbacks
  ) {
    // Cancel previous monitoring
    this.activitySub?.unsubscribe();

    if (typeof window === 'undefined') return;

    // Use passive event listeners where possible for better performance
    const userActivity$ = merge(
      fromEvent(window, 'mousemove', { passive: true }),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'click'),
      fromEvent(window, 'scroll', { passive: true }),
      fromEvent(window, 'touchstart', { passive: true })
    ).pipe(
      throttleTime(this.ACTIVITY_CHECK_INTERVAL_MS, undefined, {
        leading: true,
        trailing: false
      })
    );

    this.activitySub = userActivity$.subscribe(() => {
      // Get the current token via getter to avoid closure capture issues
      const currentToken = getToken();
      const seconds = secondsUntilExpiry(currentToken);

      // Only attempt to refresh if:
      // 1. There is a valid token
      // 2. Less than MIN_TIME_BEFORE_REFRESH_SEC remaining until expiration
      // 3. Not already in the process of refreshing
      if (
        seconds !== null &&
        seconds > 0 &&
        seconds < this.MIN_TIME_BEFORE_REFRESH_SEC &&
        !this.refreshInFlight$
      ) {
        this.refreshToken(callbacks).subscribe();
      }
    });
  }
}
